<template>
  <div id="div_teamcard" class="min-w-0">
    <!-- Summary row -->
    <div
      class="card p-3 sm:p-4 flex flex-col gap-2 cursor-pointer min-w-0"
    >
      <div class="flex flex-row flex-wrap items-start justify-between gap-2">
        <div class="min-w-0 flex-1">
          <h2 class="text-xl sm:text-2xl font-medium m-0 break-words">{{ teamName }}</h2>
          <p
            class="text-sm sm:text-base text-[var(--color-disabled)] mt-1 mb-0"
            id="txt_teamMitglieder"
          >
            {{ $t('SingleEditTeamComponent.members') + memberCount }}
          </p>
        </div>
        <div
          class="flex flex-row items-center gap-2 shrink-0 text-[var(--color-disabled)]"
          @click.stop
        >
          <button
            v-if="checkPermissionForTeamAdministration"
            type="button"
            class="p-2 rounded-lg hover:text-[var(--color-orange)] transition-colors bg-transparent border-0 cursor-pointer"
            v-tooltip.right="{ value: $t('SingleEditTeamComponent.displayTeamspace'), class: 'customer-sidebar-tooltip' }"
            @click="showAndHideTeam"
            @touchend="autoHidePrimeTooltip"
          >
            <font-awesome-icon :icon="eyeIconToDisplay" class="text-lg sm:text-xl" />
          </button>
          <button
            type="button"
            class="p-2 rounded-lg hover:text-[var(--color-orange)] transition-colors bg-transparent border-0 cursor-pointer"
            @click.prevent="toggleDetails"
          >
            <font-awesome-icon icon="fa-pen" class="text-lg sm:text-xl" />
          </button>
        </div>
      </div>
      <!-- Expanded editor -->
    <div v-if="showContainer" class="p-3 sm:p-5 mt-3">
      <vee-form :validation-schema="schema" @submit="save" v-slot="{ errors }">
        <div class="mb-4 sm:mb-6">
          <label for="txt_subject" class="text-base sm:text-lg font-medium">
            {{ $t('SingleEditTeamComponent.editTeamName') }}
          </label>
          <vee-field v-model="teamName" name="subject" v-slot="{ field }">
            <InputText
              id="txt_subject"
              v-bind="field"
              type="text"
              class="w-full text-sm sm:text-base"
              :class="{ 'border border-red-500': !!errors.subject }"
            />
          </vee-field>
          <error-message name="subject" class="text-red-600 text-sm mt-1 block" />
        </div>

        <!-- Team leads above pick list so the overlay stacks above the member list; options = all participants -->
        <div class="flex flex-col gap-6 mt-1">
          <div v-if="checkPermissionForTeamAdministration" class="flex flex-col gap-2 min-w-0 relative z-[2]">
            <label class="text-base sm:text-lg font-medium" for="ms_team_leads">
              {{ $t('SingleEditTeamComponent.teamLeads') }}
            </label>
            <MultiSelect
              id="ms_team_leads"
              v-model="leadIdsModel"
              :options="leaderParticipantOptions"
              option-label="name"
              option-value="_id"
              data-key="_id"
              filter
              display="chip"
              append-to="body"
              panel-class="team-leads-multiselect-panel"
              :placeholder="$t('SingleEditTeamComponent.searchPlaceholders.leads')"
              class="w-full"
              :max-selected-labels="6"
            >
              <template #option="{ option }">
                <span>{{ formatUserDisplayName(option) }}</span>
              </template>
            </MultiSelect>
            <p class="text-xs text-[var(--color-disabled)] m-0 leading-snug">
              {{ $t('SingleEditTeamComponent.teamLeadsHelp') }}
            </p>
          </div>

          <PickList
            v-model="pickListModel"
            data-key="_id"
            :show-source-controls="false"
            :show-target-controls="false"
            scroll-height="16rem"
            breakpoint="960px"
            class="w-full team-assignment-picklist relative z-0"
            :meta-key-selection="false"
          >
            <template #sourceheader>
              <span class="font-medium text-sm sm:text-base">{{
                $t('SingleEditTeamComponent.generalPlatformUser')
              }}</span>
            </template>
            <template #targetheader>
              <span class="font-medium text-sm sm:text-base">{{
                $t('SingleEditTeamComponent.teamMembers')
              }}</span>
            </template>
            <template #option="{ option }">
              <div class="flex items-center gap-2 min-w-0 py-0.5">
                <img
                  class="w-8 h-8 rounded-full shrink-0 object-cover"
                  src="@/assets/smiley-profil-bild.png"
                  alt=""
                >
                <span class="text-sm truncate">{{ formatUserDisplayName(option) }}</span>
              </div>
            </template>
          </PickList>
        </div>

        <div
          class="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4 mt-6 sm:mt-8 pt-4 border-t border-[var(--color-light-grey)] border-opacity-50"
        >
          <div
            v-if="checkPermissionForTeamAdministration"
            class="flex flex-col sm:flex-row sm:flex-wrap gap-4 justify-center sm:justify-start"
          >
            <div class="flex flex-row items-center gap-3">
              <span class="text-sm sm:text-base">{{ $t('SingleEditTeamComponent.displayTeamspace') }}</span>
              <ToggleSwitch
                v-model="displayTeamspace"
                @update:model-value="$emit('input', $event)"
              />
            </div>
            <div class="flex flex-row items-center gap-3">
              <span class="text-sm sm:text-base">{{ $t('SingleEditTeamComponent.isAdminTeam') }}</span>
              <ToggleSwitch v-model="isAdminTeam" />
            </div>
          </div>
          <div class="flex flex-row items-center gap-2">
            <Button class="text-red-600 bg-none rounded-full w-full sm:w-fit min-h-10 my-0 mx-0 sm:mx-4 px-4 py-2 text-sm sm:text-base
            hover:text-red-700" :label="this.$t('SingleEditTeamComponent.deleteTeam')"
              @click.prevent="deleteTeam" icon="pi pi-trash" unstyled/>
            <Button type="submit" name="save" id="btn_save" :disabled="isSubmitting"
              class="btn-primary-accent w-full sm:w-auto min-h-10 px-6 py-2 text-sm sm:text-base rounded-full sm:ml-auto"
              unstyled>
              {{ getSaveButtonText }}
            </Button>
          </div>
        </div>
      </vee-form>
    </div>
    </div>

    
  </div>
</template>

<script>
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import ToggleSwitch from 'primevue/toggleswitch';
import PickList from 'primevue/picklist';
import MultiSelect from 'primevue/multiselect';
import {
  TENANT_ROLE_ADMIN,
  TENANT_ROLE_COMPANY_SUPERUSER,
  userHasAnyRole,
} from '@/utils/userRoles';

function normalizeUserList(arr) {
  return Array.isArray(arr) ? arr.filter((x) => x != null) : [];
}

export default {
  name: 'SingleEditTeamComponent',
  components: {
    Button,
    InputText,
    ToggleSwitch,
    PickList,
    MultiSelect,
  },
  props: ['allUsers', 'leadedTeam'],
  data() {
    return {
      showContainer: false,
      leaders: normalizeUserList(this.leadedTeam.leaders),
      members: normalizeUserList(this.leadedTeam.members),
      users: normalizeUserList(this.leadedTeam.generalUsers),
      teamName: this.leadedTeam.name,
      schema: {
        subject: 'required',
      },
      isSubmitting: false,
      showPopup: false,
      popupMessage: this.$t('SingleEditTeamComponent.popup.deleteMessage'),
      deleteButtonText: this.$t('SingleEditTeamComponent.popup.deleteButton'),
      cancelButtonText: this.$t('SingleEditTeamComponent.popup.cancelButton'),
      displayTeamspace: (this.leadedTeam.displayTeamspace !== undefined
        ? this.leadedTeam.displayTeamspace : true),
      isAdminTeam: this.leadedTeam.isAdminTeam === true,
    };
  },
  watch: {
    /** Parent maps member ids → users once `allUsers` loads; local `members` is only set in data(). */
    leadedTeam: {
      handler(next) {
        const nextLeaders = normalizeUserList(next.leaders);
        const nextMembers = normalizeUserList(next.members);
        const nextUsers = normalizeUserList(next.generalUsers);
        if (!this.showContainer) {
          this.leaders = nextLeaders.map((x) => ({ ...x, isChecked: false }));
          this.members = nextMembers.map((x) => ({ ...x, isChecked: false }));
          this.users = nextUsers.map((x) => ({ ...x, isChecked: false }));
          this.teamName = next.name;
          if (next.displayTeamspace !== undefined) this.displayTeamspace = next.displayTeamspace;
          this.isAdminTeam = next.isAdminTeam === true;
          return;
        }
        if (nextMembers.length > 0 && this.members.length === 0) {
          this.leaders = nextLeaders.map((x) => ({ ...x, isChecked: false }));
          this.members = nextMembers.map((x) => ({ ...x, isChecked: false }));
          this.users = nextUsers.map((x) => ({ ...x, isChecked: false }));
        }
      },
      deep: true,
    },
  },
  computed: {
    memberCount() {
      return normalizeUserList(this.leadedTeam.members).length;
    },
    getSaveButtonText() {
      return this.leadedTeam._id
        ? this.$t('SingleEditTeamComponent.save') : this.$t('SingleEditTeamComponent.saveInit');
    },
    checkPermissionForTeamAdministration() {
      return userHasAnyRole(
        { roles: this.$store.getters.getUserRoles },
        [TENANT_ROLE_ADMIN, TENANT_ROLE_COMPANY_SUPERUSER],
      );
    },
    /** Everyone in the assignment UI (pool + team); leaders are chosen from here and must be members. */
    leaderParticipantOptions() {
      const byId = new Map();
      normalizeUserList(this.members).forEach((u) => {
        if (u && u._id) byId.set(u._id, { ...u, isChecked: false });
      });
      normalizeUserList(this.users).forEach((u) => {
        if (u && u._id && !byId.has(u._id)) byId.set(u._id, { ...u, isChecked: false });
      });
      return Array.from(byId.values()).sort((a, b) => this.formatUserDisplayName(a)
        .localeCompare(this.formatUserDisplayName(b), undefined, { sensitivity: 'base' }));
    },
    /** PickList v-model: [available users, team members] */
    pickListModel: {
      get() {
        return [this.users, this.members];
      },
      set(next) {
        if (!Array.isArray(next) || next.length !== 2) return;
        const [u, m] = next;
        const clean = (arr) => (Array.isArray(arr)
          ? arr.filter((x) => x != null).map((x) => ({ ...x, isChecked: false }))
          : []);
        this.users = clean(u);
        this.members = clean(m);
        this.leaders = this.leaders.filter(
          (l) => l && this.members.some((mem) => mem && mem._id === l._id),
        );
      },
    },
    /** MultiSelect for leads: ids must be members; selecting someone from the pool adds them to members. */
    leadIdsModel: {
      get() {
        return this.leaders.filter((l) => l != null).map((l) => l._id);
      },
      set(ids) {
        const idSet = new Set(ids || []);
        let nextUsers = normalizeUserList(this.users).map((x) => ({ ...x, isChecked: false }));
        let nextMembers = normalizeUserList(this.members).map((x) => ({ ...x, isChecked: false }));
        const poolById = new Map();
        nextMembers.forEach((u) => {
          if (u && u._id) poolById.set(u._id, u);
        });
        nextUsers.forEach((u) => {
          if (u && u._id && !poolById.has(u._id)) poolById.set(u._id, u);
        });
        idSet.forEach((id) => {
          if (!nextMembers.some((m) => m && m._id === id)) {
            const person = poolById.get(id);
            if (person) {
              nextUsers = nextUsers.filter((u) => !u || u._id !== id);
              nextMembers = [...nextMembers, { ...person, isChecked: false }];
            }
          }
        });
        this.users = nextUsers;
        this.members = nextMembers;
        this.leaders = nextMembers.filter((m) => m && idSet.has(m._id));
      },
    },
    eyeIconToDisplay() {
      return this.displayTeamspace ? 'fa-eye' : 'fa-eye-slash';
    },
  },
  methods: {
    toggleDetails() {
      this.showContainer = !this.showContainer;
      if (this.showContainer) {
        this.$nextTick(() => {
          this.syncLeadersIntoMembersFromProps();
        });
      }
    },
    /** Every leader must be a member; pull missing leaders into members (and out of the pool). */
    syncLeadersIntoMembersFromProps() {
      let nextMembers = normalizeUserList(this.members).map((x) => ({ ...x, isChecked: false }));
      let nextUsers = normalizeUserList(this.users).map((x) => ({ ...x, isChecked: false }));
      const memberIds = new Set(nextMembers.map((m) => m?._id));
      const toPullIn = normalizeUserList(this.leaders).filter((l) => l && l._id && !memberIds.has(l._id));
      if (!toPullIn.length) return;
      const pullIds = new Set(toPullIn.map((l) => l._id));
      nextUsers = nextUsers.filter((u) => !u || !pullIds.has(u._id));
      this.members = [...nextMembers, ...toPullIn.map((l) => ({ ...l, isChecked: false }))];
      this.users = nextUsers;
    },
    formatUserDisplayName(user) {
      if (!user) return '';
      if (user.name) return user.name;
      const fn = user.firstName || '';
      const ln = user.lastName || '';
      return `${fn} ${ln}`.trim() || String(user._id || '');
    },
    async save(submitValues) {
      if (submitValues && typeof submitValues === 'object' && submitValues.subject !== undefined) {
        this.teamName = submitValues.subject;
      }
      try {
        this.isSubmitting = true;
        const leaders = this.leaders.filter((e) => e != null).map((e) => e._id);
        const members = this.members.filter((e) => e != null).map((e) => e._id);
        if (this.leadedTeam._id) {
          await this.$store.dispatch('updateTeam', {
            team: {
              _id: this.leadedTeam._id,
              class: 'team_element',
              type: 'team',
              name: this.teamName,
              leaders,
              members,
              displayTeamspace: this.displayTeamspace,
              isAdminTeam: this.isAdminTeam,
            },
          });
          this.$toast?.add({
            severity: 'success',
            summary: this.$t('ToastComponent.teamManagement.teamUpdated'),
            detail: '',
            life: 3000,
          });
        } else {
          await this.$store.dispatch('createTeam', {
            team: {
              name: this.teamName,
              leaders,
              members,
              displayTeamspace: this.displayTeamspace,
              isAdminTeam: this.isAdminTeam,
            },
          });
          this.$toast?.add({
            severity: 'success',
            summary: this.$t('ToastComponent.teamManagement.teamCreated'),
            detail: '',
            life: 3000,
          });
        }
        this.$store.commit('resetTeamManagement');
        this.isSubmitting = false;
        this.showContainer = false;
        await this.$store.dispatch('getAllUsers');
        await this.$store.dispatch('getLeadedTeams');
      } catch (error) {
        this.isSubmitting = false;
        this.$toast?.add({
          severity: 'error',
          summary: error.response?.data?.message || error.message,
          detail: '',
          life: 3000,
        });
      }
    },
    deleteTeam() {
      this.showPopup = true;
      this.$confirm.require({
        message: this.popupMessage,
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        rejectProps: {
          label: this.cancelButtonText,
          severity: 'secondary',
          outlined: true,
        },
        acceptProps: {
          label: this.deleteButtonText,
        },
        accept: this.onPopupConfirm,
        reject: this.onPopupCancel,
      });
    },
    showAndHideTeam() {
      this.autoHidePrimeTooltip();
      this.displayTeamspace = !this.displayTeamspace;
      if (!this.showContainer) {
        this.save();
      }
      // Tooltip often mounts after click/touchend (delayed synthetic mouseenter on touch).
      
    },
    /**
     * PrimeVue only listens to mouseenter/click — on mobile, mouseenter can fire hundreds of ms
     * after touchend, so a single 500ms clear (CustomerSidebar pattern) runs too early and misses
     * the overlay. Staggered passes + cleanup after the click handler cover both cases.
     */
    autoHidePrimeTooltip() {
      setTimeout(() => {
        document.querySelectorAll('.p-tooltip').forEach((el) => el.remove());
      }, 500);
    },
    onToggleTeamspace() {
      this.displayTeamspace = !this.displayTeamspace;
      this.$emit('input', this.displayTeamspace);
    },
    onPopupCancel() {
      this.showPopup = false;
    },
    async onPopupConfirm() {
      try {
        this.showPopup = false;
        if (this.leadedTeam._id) {
          const data = {
            teamID: this.leadedTeam._id,
          };
          await this.$store.dispatch('deleteTeam', data);
        }
        this.$toast?.add({
          severity: 'success',
          summary: this.$t('SingleEditTeamComponent.toast.successfulDelete'),
          detail: '',
          life: 3000,
        });
        this.$store.commit('resetTeamManagement');
        await this.$store.dispatch('getAllUsers');
        await this.$store.dispatch('getLeadedTeams');
      } catch (error) {
        this.$toast?.add({
          severity: 'error',
          summary: this.$t('SingleEditTeamComponent.toast.errorDelete'),
          detail: '',
          life: 3000,
        });
      }
    },
  },
};
</script>

<style scoped>
.scroll-touch {
  -webkit-overflow-scrolling: touch;
}

.customer-sidebar-tooltip {
  width: max-content !important;
  max-width: none !important;
  white-space: nowrap !important;
  word-break: keep-all !important;
  overflow-wrap: normal !important;
}
</style>

<!-- Panel is portaled to body; keep it above PickList scrollport / stacking contexts -->
<style>
.team-leads-multiselect-panel {
  z-index: 5000 !important;
}
</style>
